/**
 * Organization Context
 *
 * Provides multi-tenant organization state throughout the application.
 * Manages current org, org switching, and org-scoped API calls.
 */
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';

export interface OrgInfo {
  id: string;
  name: string;
  slug: string;
  plan: string;
  role: string;
  logo_url?: string;
}

interface OrgContextType {
  currentOrg: OrgInfo | null;
  organizations: OrgInfo[];
  isLoading: boolean;
  switchOrg: (orgId: string) => Promise<void>;
  refreshOrgs: () => Promise<void>;
}

const OrganizationContext = createContext<OrgContextType | undefined>(undefined);

const API_BASE_URL = import.meta.env.VITE_API_URL || '';
const ACCESS_TOKEN_KEY = 'brewflow_access_token';
const REFRESH_TOKEN_KEY = 'brewflow_refresh_token';
const TOKEN_EXPIRY_KEY = 'brewflow_token_expiry';
const ORG_ID_KEY = 'brewflow_current_org_id';
const ORGS_KEY = 'brewflow_organizations';

export const OrganizationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, isAuthenticated } = useAuth();
  const [currentOrg, setCurrentOrg] = useState<OrgInfo | null>(null);
  const [organizations, setOrganizations] = useState<OrgInfo[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  /**
   * Fetch user's organizations from the API
   */
  const refreshOrgs = useCallback(async () => {
    if (!isAuthenticated) return;

    setIsLoading(true);
    try {
      const token = localStorage.getItem(ACCESS_TOKEN_KEY);
      if (!token) return;

      const response = await fetch(`${API_BASE_URL}/api/v1/auth/organizations`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) return;

      const result = await response.json();
      const orgs: OrgInfo[] = result.data || [];
      setOrganizations(orgs);
      localStorage.setItem(ORGS_KEY, JSON.stringify(orgs));

      // Set current org from user's current_org_id or first org
      const storedOrgId = localStorage.getItem(ORG_ID_KEY);
      const targetOrgId = storedOrgId || (user as any)?.current_org_id;

      if (targetOrgId) {
        const org = orgs.find((o) => o.id === targetOrgId);
        if (org) {
          setCurrentOrg(org);
          localStorage.setItem(ORG_ID_KEY, org.id);
        } else if (orgs.length > 0) {
          setCurrentOrg(orgs[0]);
          localStorage.setItem(ORG_ID_KEY, orgs[0].id);
        }
      } else if (orgs.length > 0) {
        setCurrentOrg(orgs[0]);
        localStorage.setItem(ORG_ID_KEY, orgs[0].id);
      }
    } catch (error) {
      console.error('Failed to fetch organizations:', error);
      // Try to restore from localStorage
      const storedOrgs = localStorage.getItem(ORGS_KEY);
      if (storedOrgs) {
        try {
          const orgs = JSON.parse(storedOrgs);
          setOrganizations(orgs);
          const storedOrgId = localStorage.getItem(ORG_ID_KEY);
          if (storedOrgId) {
            const org = orgs.find((o: OrgInfo) => o.id === storedOrgId);
            if (org) setCurrentOrg(org);
          }
        } catch {
          // ignore parse errors
        }
      }
    } finally {
      setIsLoading(false);
    }
  }, [isAuthenticated, user]);

  /**
   * Switch to a different organization
   */
  const switchOrg = useCallback(async (orgId: string) => {
    const token = localStorage.getItem(ACCESS_TOKEN_KEY);
    if (!token) return;

    setIsLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/v1/auth/switch-org`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ org_id: orgId }),
      });

      if (!response.ok) {
        throw new Error('Failed to switch organization');
      }

      const result = await response.json();
      const tokenData = result.data;

      // Update tokens with new org context
      localStorage.setItem(ACCESS_TOKEN_KEY, tokenData.access_token);
      localStorage.setItem(REFRESH_TOKEN_KEY, tokenData.refresh_token);
      localStorage.setItem(ORG_ID_KEY, orgId);

      const expiryTime = Date.now() + (tokenData.expires_in * 1000);
      localStorage.setItem(TOKEN_EXPIRY_KEY, expiryTime.toString());

      // Update current org in state
      const org = organizations.find((o) => o.id === orgId);
      if (org) {
        setCurrentOrg(org);
      }

      // Reload page to refresh all data with new org context
      window.location.reload();
    } catch (error) {
      console.error('Failed to switch organization:', error);
    } finally {
      setIsLoading(false);
    }
  }, [organizations]);

  // Fetch orgs when user authenticates
  useEffect(() => {
    if (isAuthenticated && user) {
      refreshOrgs();
    } else {
      setCurrentOrg(null);
      setOrganizations([]);
    }
  }, [isAuthenticated, user?.id]);

  return (
    <OrganizationContext.Provider
      value={{
        currentOrg,
        organizations,
        isLoading,
        switchOrg,
        refreshOrgs,
      }}
    >
      {children}
    </OrganizationContext.Provider>
  );
};

/**
 * Hook to use organization context
 */
export const useOrganization = () => {
  const context = useContext(OrganizationContext);
  if (context === undefined) {
    throw new Error('useOrganization must be used within an OrganizationProvider');
  }
  return context;
};
